import * as styles from "./ParagraphTextWithArticles.module.scss";

import cx from "classnames";
import { graphql } from "gatsby";
import React, { type HTMLAttributes, useState } from "react";
import { Chevron } from "../../../components/common/Icons";
import ProcessedImage from "../../../components/common/ProcessedImage/ProcessedImage";
import SectionArticleOrEventCards from "../../../containers/sections/SectionArticleOrEventCards";
import WrappedSection, {
	UnwrappedWrappedSection,
} from "../../WrappedSection/WrappedSection";
import SectionBodyCondition from "../../sections/SectionBodyCondition/SectionBodyCondition";

const MAX_CHARACTER_COUNT = 220;

export interface ParagraphTextWithArticlesProps
	extends HTMLAttributes<HTMLElement> {
	heading?: string;
	introText: string;
	items: DogsTrust.SignPostCardProps[];
	isSubComponent?: boolean;
	showBodyConditionSection?: boolean;
	subComponentTheme?: string;
	images?: DogsTrust.MediaImageProps[];
	jumpLinksTitle?: string;
}

export const getParagraphTextWithArticlesProps = ({
	field_section_title,
	field_jump_links_title,
	field_intro_copy,
	field_expandable_enable_bc,
	relationships: {
		field_article_picker,
		field_image,
		field_expandable_body_condition,
	},
}: Queries.ParagraphTextWithArticlesDataFragment): ParagraphTextWithArticlesProps => ({
	heading: field_section_title,
	jumpLinksTitle: field_jump_links_title,
	introText: field_intro_copy.value,
	images: field_image?.map(
		(image) =>
			({
				src: image?.field_media_image?.uri || "",
				alt: image?.field_media_image?.alt || "",
			}) as DogsTrust.MediaImageProps,
	),
	items: field_article_picker.map(
		(article) =>
			({
				priority: article.field_node_weight,
				title: article.title,
				body: article.field_short_description,
				buttonLabel: article.field_short_link_text,
				buttonUrl: article.path.alias,
				imgSrc:
					article.relationships?.field_featured_image?.field_media_image?.uri ||
					"",
				label: article.relationships?.field_category_type?.name,
			}) as DogsTrust.SignPostCardProps,
	),
	showBodyConditionSection: field_expandable_enable_bc,
});
export const ParagraphTextWithArticles: React.FC<
	ParagraphTextWithArticlesProps
> = ({
	heading,
	introText,
	items,
	isSubComponent = false,
	subComponentTheme = "light" || "alternative",
	images,
	showBodyConditionSection,
	className,
	jumpLinksTitle,
}) => {
	const getShortText = () => {
		if (introText.length > MAX_CHARACTER_COUNT)
			return `${introText.slice(0, MAX_CHARACTER_COUNT)}...`;
		return introText;
	};

	const getShowArticles = () => {
		if (introText.length > MAX_CHARACTER_COUNT) return false;
		return true;
	};

	const canShowMore = introText.length > MAX_CHARACTER_COUNT;

	const [showAllText, setShowAllText] = useState(false);
	const [showArticles, setShowArticles] = useState(getShowArticles());

	const doubleAction = () => {
		setShowAllText((prevShowAll) => !prevShowAll);
		setShowArticles((prevShowArticles) => !prevShowArticles);
	};

	const Wrapper = isSubComponent ? UnwrappedWrappedSection : WrappedSection;

	return (
		<Wrapper sectionName="ParagraphTextWithArticles">
			<div className={cx(className, styles.paragraphtextarticlesinner)}>
				<div
					className={
						canShowMore
							? styles.textwitharticles
							: styles.textwitharticlesnospace
					}
				>
					{heading && (
						<div className={styles.titlewrapper}>
							{isSubComponent ? (
								<h3 data-variation="hideFromJumpLinks" className={styles.title}>
									{heading}
								</h3>
							) : jumpLinksTitle ? (
								<h2
									data-jumplinkstitle={jumpLinksTitle}
									className={styles.title}
								>
									{heading}
								</h2>
							) : (
								<h2 className={styles.title}>{heading}</h2>
							)}
						</div>
					)}
					{!showAllText && (
						<div
							aria-hidden={showAllText}
							className={styles.text}
							// biome-ignore lint/security/noDangerouslySetInnerHtml: Safe source
							dangerouslySetInnerHTML={{
								__html: getShortText(),
							}}
						/>
					)}
					{introText.length > MAX_CHARACTER_COUNT && (
						<div
							aria-hidden={!showAllText}
							className={showAllText ? styles.text : styles.visibilityhidden}
							// biome-ignore lint/security/noDangerouslySetInnerHtml: Safe source
							dangerouslySetInnerHTML={{
								__html: introText,
							}}
						/>
					)}

					{canShowMore && !showAllText && (
						<div
							className={
								subComponentTheme === "alternative"
									? styles.gradientalt
									: styles.gradient
							}
						/>
					)}
					{items?.length > 0 && (
						<div
							aria-hidden={!showAllText}
							className={
								showArticles ? styles.articles : styles.visibilityhidden
							}
						>
							<SectionArticleOrEventCards
								items={items}
								withoutMargins={true}
								forceFlatCards={false}
								noWrapper={true}
							/>
						</div>
					)}

					{images?.length > 0 && (
						<div
							aria-hidden={!showAllText}
							className={
								showArticles && images
									? styles.imagescontainer
									: styles.visibilityhidden
							}
						>
							{images.map((image) => (
								<ProcessedImage
									key={image.src}
									src={image.src}
									dimensions="600x450"
									width={600}
									height={450}
									alt={image.alt}
									loading={"lazy"}
								/>
							))}
						</div>
					)}
					{showBodyConditionSection && (
						<SectionBodyCondition
							isChild={true}
							aria-hidden={!showAllText}
							className={!showAllText && styles.visibilityhidden}
						/>
					)}
					{canShowMore && (
						<button
							type="button"
							className={styles.showmore}
							onClick={() => doubleAction()}
							tabIndex={0}
							onKeyDown={(e) => e.key === "Enter" && doubleAction()}
						>
							<p>{`${!showAllText ? "Show more" : "Show less"}`}</p>
							<Chevron
								className={styles.chevron}
								fill="#222"
								height={"20px"}
								width={"20px"}
								rotation={showAllText ? 0 : 180}
							/>
						</button>
					)}
				</div>
			</div>
		</Wrapper>
	);
};

export default ParagraphTextWithArticles;

export const fragment = graphql`
    fragment ParagraphTextWithArticlesData on paragraph__text_with_articles {
        __typename
        id
        field_section_title
		field_jump_links_title
        field_intro_copy {
            value
        }
		field_expandable_enable_bc
        relationships {
			field_image {
                field_media_image {
                    alt
                    uri
                }
            }
            field_article_picker {
                title
                path {
                    alias
                }
                field_node_weight
                field_short_description
                field_short_link_text

                relationships {
                    field_category_type {
                        name
                    }
                    field_featured_image {
                        field_media_image {
                            uri
                            alt
                            title
                            width
                            height
                        }
                    }
                }
            }
            field_expandable_body_condition {
                ...ParagraphSectionData
            }
        }
    }
`;
